import { create } from "zustand";
import { setTelemetryOptOut as setTelemetryClientOptOut } from "../telemetry/telemetry";
import { CAMPAIGN_PHASES, clampPhaseIndex } from "./campaign";
import {
  getTutorialByPhase,
  getTutorialForCampaignPhase,
  getTutorialStorageKey,
} from "./tutorials";
import type {
  CutDirection,
  ObstacleType,
  TargetAppearance,
  TargetSize,
  TargetSpeed,
} from "./beatmap";
import type { TutorialAttemptFeedback } from "./tutorialCheck";

export type Phase =
  | "menu"
  | "survival-intro"
  | "teach"
  | "demo"
  | "consolidate"
  | "countdown"
  | "playing"
  | "paused"
  | "win"
  | "fail"
  | "kata"
  | "story";
export type GameMode = "campaign" | "survival";
export type WieldMode = "single" | "dual";
export type SingleHand = "left" | "right";
export type HeightMode = "standing" | "seated";
export type Grade = "IPPON" | "PERFECT" | "GREAT" | "GOOD" | "LATE" | "MISS";
export type TargetRunResult = "hit" | "missed" | "wrong-direction";
export type ObstacleRunResult = "cleared" | "hit";
export type ResultShareStatus =
  | "idle"
  | "sharing"
  | "shared"
  | "fallback"
  | "failed";
export type SurvivalAudioStatus = "idle" | "loading" | "ready" | "error";

export interface TargetRunEvent {
  id: number;
  t: number;
  dir: CutDirection;
  hand: "left" | "right" | "any";
  size: TargetSize;
  speed: TargetSpeed;
  appearance: TargetAppearance;
  seq?: number;
  result: TargetRunResult;
  grade: Grade;
  points: number;
  timingError: number | null;
  tenouchi: boolean;
  comboAfter: number;
  // Calibration fields — optional so runStats.ts/ResultScreen.tsx (which
  // predate these) keep working untouched. Populated at the impact site in
  // Block.tsx and consumed only by src/telemetry/runSummary.ts, which
  // reduces them to per-run aggregates (median/percentiles/histograms),
  // never sent per-note. See CLAUDE.md: timing windows, MIN_SLASH_SPEED and
  // TENOUCHI_SPEED are unverified without real headset data — this is what
  // closes that gap.
  timingSigned?: number; // t - note.t: unlike timingError, preserves early/late sign
  snap?: number;
  bladeSpeed?: number;
  directionAlignment?: number;
  centerAccuracy?: number;
}

export interface ObstacleRunEvent {
  id: number;
  t: number;
  type: ObstacleType;
  result: ObstacleRunResult;
}

function loadUnlocked(): number[] {
  try {
    return JSON.parse(localStorage.getItem("sq_unlockedKatas") ?? "[]");
  } catch {
    return [];
  }
}

function loadCampaignProgress(): {
  unlockedPhaseIndex: number;
  phaseBest: number[];
} {
  try {
    const raw = JSON.parse(localStorage.getItem("sq_campaignProgress") ?? "{}");
    return {
      unlockedPhaseIndex: clampPhaseIndex(Number(raw.unlockedPhaseIndex ?? 0)),
      phaseBest: Array.isArray(raw.phaseBest)
        ? raw.phaseBest.map((n: unknown) => Number(n) || 0)
        : [],
    };
  } catch {
    return { unlockedPhaseIndex: 0, phaseBest: [] };
  }
}

function loadTutorialSeen(): string[] {
  try {
    const raw = JSON.parse(localStorage.getItem("sq_tutorialSeen") ?? "[]");
    return Array.isArray(raw)
      ? raw.filter((key) => typeof key === "string")
      : [];
  } catch {
    return [];
  }
}

function saveLocalStorage(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    console.warn(`Unable to save local progress: ${key}`);
  }
}

function saveCampaignProgress(unlockedPhaseIndex: number, phaseBest: number[]) {
  saveLocalStorage(
    "sq_campaignProgress",
    JSON.stringify({ unlockedPhaseIndex, phaseBest }),
  );
}

function saveTutorialSeen(seen: string[]) {
  saveLocalStorage("sq_tutorialSeen", JSON.stringify(seen));
}

export interface SurvivalRun {
  score: number;
  date: string; // ISO timestamp
}

const SURVIVAL_LEADERBOARD_SIZE = 5;

function loadSurvivalProgress(): { best: number; runs: SurvivalRun[] } {
  try {
    const raw = JSON.parse(localStorage.getItem("sq_survivalBest") ?? "{}");
    return {
      best: Number(raw.best) || 0,
      runs: Array.isArray(raw.runs)
        ? raw.runs.slice(0, SURVIVAL_LEADERBOARD_SIZE)
        : [],
    };
  } catch {
    return { best: 0, runs: [] };
  }
}

function saveSurvivalProgress(best: number, runs: SurvivalRun[]) {
  saveLocalStorage("sq_survivalBest", JSON.stringify({ best, runs }));
}

// Opt-out defaults to false (telemetry on) — see docs/observability.md and
// the privacy policy's "Network and Analytics" section for what's collected
// and why this is opt-out rather than opt-in.
function loadTelemetryOptOut(): boolean {
  try {
    return localStorage.getItem("sq_telemetryOptOut") === "1";
  } catch {
    return false;
  }
}

// Default false: the target's direction notch points the way the blade
// should travel (see DIR_ARROW_ROT in Block.tsx). true restores the v1
// "cut against the notch" reading for players who learned that convention.
function loadCutArrowInverted(): boolean {
  try {
    return localStorage.getItem("sq_cutArrowInverted") === "1";
  } catch {
    return false;
  }
}

export type GradeCounts = Record<Grade, number>;

function freshGradeCounts(): GradeCounts {
  return { IPPON: 0, PERFECT: 0, GREAT: 0, GOOD: 0, LATE: 0, MISS: 0 };
}

// Shared per-run reset fields — spread into every "start a run" transition
// (campaign start/retry/advance, Survival start/retry) so result-screen
// stats (grade breakdown, kata tally, peak combo) never leak between runs.
function freshRunState() {
  return {
    score: 0,
    combo: 0,
    missCount: 0,
    lastGrade: null as Grade | null,
    lastTenouchi: false,
    seqBonus: null as number | null,
    lastRunPassed: null as boolean | null,
    gradeCounts: freshGradeCounts(),
    peakCombo: 0,
    runKataLanded: 0,
    runKataTotal: 0,
    runKataBonus: 0,
    runTargetEvents: [] as TargetRunEvent[],
    runObstacleEvents: [] as ObstacleRunEvent[],
    resultDetailsOpen: false,
    resultShareStatus: "idle" as ResultShareStatus,
  };
}

interface GameStore {
  phase: Phase;
  mode: GameMode;
  score: number;
  combo: number;
  best: number;
  missCount: number;
  lastGrade: Grade | null;
  lastTenouchi: boolean;
  seqBonus: number | null;
  unlockedKatas: number[];
  activeKata: number;
  selectedPhaseIndex: number;
  unlockedPhaseIndex: number;
  phaseBest: number[];
  lastRunPassed: boolean | null;
  lastRunPhaseIndex: number;
  lastRunThreshold: number;
  survivalBest: number;
  survivalRuns: SurvivalRun[];
  survivalElapsed: number;
  survivalTrackIndex: number;
  survivalIntensity: number;
  survivalAudioStatus: SurvivalAudioStatus;
  survivalAudioLoaded: number;
  survivalAudioTotal: number;
  lastSurvivalWaves: number;
  lastSurvivalDuration: number;
  lastSurvivalTrackIndex: number;
  lastSurvivalWasRecord: boolean;
  lastSurvivalRunDate: string | null;
  gradeCounts: GradeCounts;
  peakCombo: number;
  runKataLanded: number;
  runKataTotal: number;
  runKataBonus: number;
  runTargetEvents: TargetRunEvent[];
  runObstacleEvents: ObstacleRunEvent[];
  resultDetailsOpen: boolean;
  resultShareStatus: ResultShareStatus;
  settings: {
    wield: WieldMode;
    singleHand: SingleHand;
    height: HeightMode;
    // Unlike wield/singleHand/height (chosen per session), this persists —
    // it's a fixed personal reading preference, not a headset setup choice.
    cutArrowInverted: boolean;
  };
  // Opt-out (not opt-in) for anonymous usage telemetry — see
  // docs/observability.md. Persisted, unlike bloomEnabled/swordGlowEnabled.
  telemetryOptOut: boolean;
  // Visual A/B toggles. Not player settings and not persisted. They live here
  // because Katana is registered as an xrStore controller component and can't
  // receive props from App's JSX.
  bloomEnabled: boolean;
  swordGlowEnabled: boolean;
  // World-space menu's ensō cursor. Same "cross-component, not persisted"
  // category as the toggles above — MenuScreen3D (rendering) and
  // MenuNavControls (thumbstick input) are sibling components (one inside
  // <XR>, one outside) that only share state through this store.
  menuCursor: "campaign" | "survival" | "exit";
  menuExitConfirm: boolean;
  menuExitCursor: "cancel" | "exit";
  tutorialSeen: string[];
  activeTutorialPhase: number | null;
  tutorialLineIndex: number;
  tutorialFeedback: TutorialAttemptFeedback | null;
  tutorialAttemptCount: number;
  tutorialPracticeResetKey: number;
  tutorialReviewOnly: boolean;

  setPhase: (p: Phase) => void;
  selectPhase: (index: number) => void;
  startCampaignPhase: (index?: number) => void;
  reviewCampaignTutorial: (index?: number, tutorialPhase?: number) => void;
  completeCampaignRun: () => void;
  retryCampaignPhase: () => void;
  advanceCampaignPhase: () => void;
  goToStory: () => void;
  finishStory: () => void;
  advanceTutorial: () => void;
  backTutorial: () => void;
  finishTutorialDemo: () => void;
  skipTutorial: () => void;
  recordTutorialAttempt: (feedback: TutorialAttemptFeedback) => void;
  clearTutorialFeedback: () => void;
  openSurvivalIntro: () => void;
  startSurvival: () => void;
  setSurvivalAudioProgress: (
    status: SurvivalAudioStatus,
    loaded: number,
    total: number,
  ) => void;
  retrySurvivalAudio: () => void;
  setSurvivalProgress: (progress: {
    elapsed: number;
    trackIndex: number;
    intensity: number;
  }) => void;
  completeSurvivalRun: () => void;
  retrySurvival: () => void;
  setWield: (w: WieldMode) => void;
  setSingleHand: (h: SingleHand) => void;
  setHeightMode: (h: HeightMode) => void;
  setCutArrowInverted: (value: boolean) => void;
  setBloomEnabled: (enabled: boolean) => void;
  toggleBloom: () => void;
  setTelemetryOptOut: (value: boolean) => void;
  toggleSwordGlow: () => void;
  setMenuCursor: (cursor: "campaign" | "survival" | "exit") => void;
  setMenuExitConfirm: (open: boolean) => void;
  setMenuExitCursor: (cursor: "cancel" | "exit") => void;
  addScore: (points: number) => void;
  incrementCombo: () => void;
  resetCombo: () => void;
  addMiss: () => void;
  setLastGrade: (g: Grade | null) => void;
  setTenouchi: (v: boolean) => void;
  setSeqBonus: (n: number | null) => void;
  recordGrade: (g: Grade) => void;
  recordTargetEvent: (event: TargetRunEvent) => void;
  recordObstacleEvent: (event: ObstacleRunEvent) => void;
  setResultDetailsOpen: (open: boolean) => void;
  toggleResultDetails: () => void;
  setResultShareStatus: (status: ResultShareStatus) => void;
  setRunKataTotal: (n: number) => void;
  recordKataLanded: (bonus: number) => void;
  unlockKata: (id: number) => void;
  setActiveKata: (id: number) => void;
  resetGame: () => void;
}

const campaignProgress = loadCampaignProgress();
const survivalProgress = loadSurvivalProgress();
const tutorialSeen = loadTutorialSeen();

function markTutorialSeen(seen: string[], tutorialPhase: number): string[] {
  const key = getTutorialStorageKey(tutorialPhase);
  if (seen.includes(key)) return seen;
  const next = [...seen, key];
  saveTutorialSeen(next);
  return next;
}

function pendingTutorialForPhase(
  selectedPhaseIndex: number,
  seen: string[],
): number | null {
  if (selectedPhaseIndex === 0 && !seen.includes(getTutorialStorageKey(0))) {
    return 0;
  }

  const tutorial = getTutorialForCampaignPhase(selectedPhaseIndex);
  if (!tutorial) return null;

  const key = getTutorialStorageKey(tutorial.phase);
  return seen.includes(key) ? null : tutorial.phase;
}

function campaignEntryState(selectedPhaseIndex: number, seen: string[]) {
  const pendingTutorialPhase = pendingTutorialForPhase(
    selectedPhaseIndex,
    seen,
  );

  if (pendingTutorialPhase != null) {
    return {
      phase: "teach" as Phase,
      activeTutorialPhase: pendingTutorialPhase,
      tutorialLineIndex: 0,
      tutorialFeedback: null,
      tutorialAttemptCount: 0,
      tutorialPracticeResetKey: 0,
      tutorialReviewOnly: false,
    };
  }

  return {
    phase: "countdown" as Phase,
    activeTutorialPhase: null,
    tutorialLineIndex: 0,
    tutorialFeedback: null,
    tutorialAttemptCount: 0,
    tutorialPracticeResetKey: 0,
    tutorialReviewOnly: false,
  };
}

// V1: skip the ghost-sword demonstration phase. Keep the state helpers for v2.
const GHOST_DEMO_ENABLED = false;

function afterTutorialState(
  completedTutorialPhase: number,
  selectedPhaseIndex: number,
  seen: string[],
  reviewOnly: boolean,
) {
  const nextSeen = markTutorialSeen(seen, completedTutorialPhase);

  if (!reviewOnly && completedTutorialPhase === 0 && selectedPhaseIndex === 0) {
    const phaseOne = getTutorialByPhase(1);
    if (phaseOne && !nextSeen.includes(getTutorialStorageKey(phaseOne.phase))) {
      return {
        tutorialSeen: nextSeen,
        phase: "teach" as Phase,
        activeTutorialPhase: phaseOne.phase,
        tutorialLineIndex: 0,
        tutorialFeedback: null,
        tutorialAttemptCount: 0,
        tutorialPracticeResetKey: 0,
        tutorialReviewOnly: false,
      };
    }
  }

  return {
    tutorialSeen: nextSeen,
    phase: "countdown" as Phase,
    activeTutorialPhase: null,
    tutorialLineIndex: 0,
    tutorialFeedback: null,
    tutorialAttemptCount: 0,
    tutorialPracticeResetKey: 0,
    tutorialReviewOnly: false,
  };
}

function tutorialDemoState(tutorialPhase: number, reviewOnly: boolean) {
  return {
    phase: "demo" as Phase,
    activeTutorialPhase: tutorialPhase,
    tutorialLineIndex: 0,
    tutorialFeedback: null,
    tutorialAttemptCount: 0,
    tutorialPracticeResetKey: 0,
    tutorialReviewOnly: reviewOnly,
  };
}

function afterTutorialDemoState(
  tutorialPhase: number,
  selectedPhaseIndex: number,
  seen: string[],
  reviewOnly: boolean,
) {
  return afterTutorialState(
    tutorialPhase,
    selectedPhaseIndex,
    seen,
    reviewOnly,
  );
}

export const useGameStore = create<GameStore>((set, get) => ({
  phase: "menu",
  mode: "campaign",
  score: 0,
  combo: 0,
  best: 0,
  missCount: 0,
  lastGrade: null,
  lastTenouchi: false,
  seqBonus: null,
  unlockedKatas: loadUnlocked(),
  activeKata: 1,
  selectedPhaseIndex: campaignProgress.unlockedPhaseIndex,
  unlockedPhaseIndex: campaignProgress.unlockedPhaseIndex,
  phaseBest: campaignProgress.phaseBest,
  lastRunPassed: null,
  lastRunPhaseIndex: 0,
  lastRunThreshold: CAMPAIGN_PHASES[0].threshold,
  survivalBest: survivalProgress.best,
  survivalRuns: survivalProgress.runs,
  survivalElapsed: 0,
  survivalTrackIndex: 0,
  survivalIntensity: 0,
  survivalAudioStatus: "idle",
  survivalAudioLoaded: 0,
  survivalAudioTotal: 6,
  lastSurvivalWaves: 0,
  lastSurvivalDuration: 0,
  lastSurvivalTrackIndex: 0,
  lastSurvivalWasRecord: false,
  lastSurvivalRunDate: null,
  gradeCounts: freshGradeCounts(),
  peakCombo: 0,
  runKataLanded: 0,
  runKataTotal: 0,
  runKataBonus: 0,
  runTargetEvents: [],
  runObstacleEvents: [],
  resultDetailsOpen: false,
  resultShareStatus: "idle",
  settings: {
    wield: "single",
    singleHand: "right",
    height: "standing",
    cutArrowInverted: loadCutArrowInverted(),
  },
  telemetryOptOut: loadTelemetryOptOut(),
  bloomEnabled: false,
  swordGlowEnabled: true,
  menuCursor: "campaign",
  menuExitConfirm: false,
  menuExitCursor: "cancel",
  tutorialSeen,
  activeTutorialPhase: null,
  tutorialLineIndex: 0,
  tutorialFeedback: null,
  tutorialAttemptCount: 0,
  tutorialPracticeResetKey: 0,
  tutorialReviewOnly: false,

  setPhase: (p) => set({ phase: p }),

  selectPhase: (index) =>
    set((s) => {
      const selectedPhaseIndex = Math.min(
        clampPhaseIndex(index),
        s.unlockedPhaseIndex,
      );
      return { selectedPhaseIndex };
    }),

  startCampaignPhase: (index) =>
    set((s) => {
      const selectedPhaseIndex = Math.min(
        clampPhaseIndex(index ?? s.selectedPhaseIndex),
        s.unlockedPhaseIndex,
      );
      return {
        ...freshRunState(),
        mode: "campaign",
        selectedPhaseIndex,
        ...campaignEntryState(selectedPhaseIndex, s.tutorialSeen),
        lastRunPhaseIndex: selectedPhaseIndex,
        lastRunThreshold: CAMPAIGN_PHASES[selectedPhaseIndex].threshold,
      };
    }),

  reviewCampaignTutorial: (index, tutorialPhase) =>
    set((s) => {
      const selectedPhaseIndex = Math.min(
        clampPhaseIndex(index ?? s.selectedPhaseIndex),
        s.unlockedPhaseIndex,
      );
      const phaseToReview =
        tutorialPhase ?? getTutorialForCampaignPhase(selectedPhaseIndex)?.phase;

      if (phaseToReview == null || getTutorialByPhase(phaseToReview) == null) {
        return {};
      }

      return {
        ...freshRunState(),
        mode: "campaign",
        selectedPhaseIndex,
        phase: "teach",
        activeTutorialPhase: phaseToReview,
        tutorialLineIndex: 0,
        tutorialFeedback: null,
        tutorialAttemptCount: 0,
        tutorialPracticeResetKey: 0,
        tutorialReviewOnly: true,
        lastRunPhaseIndex: selectedPhaseIndex,
        lastRunThreshold: CAMPAIGN_PHASES[selectedPhaseIndex].threshold,
      };
    }),

  completeCampaignRun: () =>
    set((s) => {
      const phaseIndex = clampPhaseIndex(s.selectedPhaseIndex);
      const threshold = CAMPAIGN_PHASES[phaseIndex].threshold;
      const passed = s.score >= threshold;
      const nextUnlocked = passed
        ? Math.max(s.unlockedPhaseIndex, clampPhaseIndex(phaseIndex + 1))
        : s.unlockedPhaseIndex;
      const phaseBest = [...s.phaseBest];
      phaseBest[phaseIndex] = Math.max(phaseBest[phaseIndex] ?? 0, s.score);
      saveCampaignProgress(nextUnlocked, phaseBest);
      return {
        phase: "win",
        activeTutorialPhase: null,
        tutorialLineIndex: 0,
        tutorialFeedback: null,
        tutorialPracticeResetKey: 0,
        tutorialReviewOnly: false,
        unlockedPhaseIndex: nextUnlocked,
        phaseBest,
        lastRunPassed: passed,
        lastRunPhaseIndex: phaseIndex,
        lastRunThreshold: threshold,
        best: Math.max(s.best, s.score),
      };
    }),

  retryCampaignPhase: () => get().startCampaignPhase(get().lastRunPhaseIndex),

  advanceCampaignPhase: () =>
    set((s) => {
      const nextIndex = Math.min(
        clampPhaseIndex(s.lastRunPhaseIndex + 1),
        s.unlockedPhaseIndex,
      );
      return {
        ...freshRunState(),
        selectedPhaseIndex: nextIndex,
        ...campaignEntryState(nextIndex, s.tutorialSeen),
        lastRunPhaseIndex: nextIndex,
        lastRunThreshold: CAMPAIGN_PHASES[nextIndex].threshold,
      };
    }),

  goToStory: () => set({ phase: "story" }),

  finishStory: () => {
    const s = get();
    if (s.lastRunPhaseIndex < CAMPAIGN_PHASES.length - 1) {
      get().advanceCampaignPhase();
    } else {
      get().resetGame();
    }
  },

  advanceTutorial: () =>
    set((s) => {
      if (
        (s.phase !== "teach" &&
          s.phase !== "demo" &&
          s.phase !== "consolidate") ||
        s.activeTutorialPhase == null
      ) {
        return {};
      }

      if (s.phase === "teach") {
        const tutorial = getTutorialByPhase(s.activeTutorialPhase);
        const lastLineIndex = Math.max(
          0,
          (tutorial?.seijiLines.length ?? 1) - 1,
        );
        if (s.tutorialLineIndex < lastLineIndex) {
          return {
            tutorialLineIndex: s.tutorialLineIndex + 1,
            tutorialFeedback: null,
            tutorialReviewOnly: s.tutorialReviewOnly,
          };
        }

        if (GHOST_DEMO_ENABLED) {
          return tutorialDemoState(s.activeTutorialPhase, s.tutorialReviewOnly);
        }

        return afterTutorialDemoState(
          s.activeTutorialPhase,
          s.selectedPhaseIndex,
          s.tutorialSeen,
          s.tutorialReviewOnly,
        );
      }

      if (s.phase === "demo") {
        return afterTutorialDemoState(
          s.activeTutorialPhase,
          s.selectedPhaseIndex,
          s.tutorialSeen,
          s.tutorialReviewOnly,
        );
      }

      return afterTutorialState(
        s.activeTutorialPhase,
        s.selectedPhaseIndex,
        s.tutorialSeen,
        s.tutorialReviewOnly,
      );
    }),

  backTutorial: () =>
    set((s) => {
      if (s.phase !== "teach" || s.activeTutorialPhase == null) return {};
      return {
        tutorialLineIndex: Math.max(0, s.tutorialLineIndex - 1),
        tutorialFeedback: null,
      };
    }),

  finishTutorialDemo: () =>
    set((s) => {
      if (s.phase !== "demo" || s.activeTutorialPhase == null) {
        return {};
      }

      return afterTutorialDemoState(
        s.activeTutorialPhase,
        s.selectedPhaseIndex,
        s.tutorialSeen,
        s.tutorialReviewOnly,
      );
    }),

  skipTutorial: () =>
    set((s) => {
      if (
        (s.phase !== "teach" &&
          s.phase !== "demo" &&
          s.phase !== "consolidate") ||
        s.activeTutorialPhase == null
      ) {
        return {};
      }

      return afterTutorialState(
        s.activeTutorialPhase,
        s.selectedPhaseIndex,
        s.tutorialSeen,
        s.tutorialReviewOnly,
      );
    }),

  recordTutorialAttempt: (feedback) =>
    set((s) => ({
      tutorialFeedback: feedback,
      tutorialAttemptCount: s.tutorialAttemptCount + 1,
    })),

  clearTutorialFeedback: () =>
    set((s) => ({
      tutorialFeedback: null,
      tutorialPracticeResetKey: s.tutorialPracticeResetKey + 1,
    })),

  openSurvivalIntro: () =>
    set({
      mode: "survival",
      phase: "survival-intro",
      survivalAudioStatus: "idle",
      survivalAudioLoaded: 0,
      survivalAudioTotal: 6,
    }),

  startSurvival: () =>
    set((s) => {
      if (s.survivalAudioStatus !== "ready") return {};
      return {
        ...freshRunState(),
        mode: "survival",
        phase: "countdown",
        survivalElapsed: 0,
        survivalTrackIndex: 0,
        survivalIntensity: 0,
        activeTutorialPhase: null,
        tutorialLineIndex: 0,
        tutorialFeedback: null,
        tutorialPracticeResetKey: 0,
        tutorialReviewOnly: false,
      };
    }),

  setSurvivalAudioProgress: (status, loaded, total) =>
    set({
      survivalAudioStatus: status,
      survivalAudioLoaded: Math.max(0, loaded),
      survivalAudioTotal: Math.max(0, total),
    }),

  retrySurvivalAudio: () =>
    set({
      survivalAudioStatus: "idle",
      survivalAudioLoaded: 0,
      survivalAudioTotal: 6,
    }),

  setSurvivalProgress: (progress) =>
    set({
      survivalElapsed: Math.max(0, progress.elapsed),
      survivalTrackIndex: Math.max(0, progress.trackIndex),
      survivalIntensity: Math.max(0, progress.intensity),
    }),

  completeSurvivalRun: () =>
    set((s) => {
      const finalScore = s.score;
      const date = new Date().toISOString();
      const wasRecord = finalScore > s.survivalBest;
      const best = Math.max(s.survivalBest, finalScore);
      const runs = [...s.survivalRuns, { score: finalScore, date }]
        .sort((a, b) => b.score - a.score)
        .slice(0, SURVIVAL_LEADERBOARD_SIZE);
      saveSurvivalProgress(best, runs);
      return {
        phase: "fail",
        activeTutorialPhase: null,
        tutorialLineIndex: 0,
        tutorialFeedback: null,
        tutorialPracticeResetKey: 0,
        tutorialReviewOnly: false,
        survivalBest: best,
        survivalRuns: runs,
        lastSurvivalWaves: Math.max(1, Math.floor(s.survivalElapsed / 20) + 1),
        lastSurvivalDuration: s.survivalElapsed,
        lastSurvivalTrackIndex: s.survivalTrackIndex,
        lastSurvivalWasRecord: wasRecord,
        lastSurvivalRunDate: date,
        best: Math.max(s.best, finalScore),
      };
    }),

  retrySurvival: () => get().startSurvival(),

  setWield: (w) => set((s) => ({ settings: { ...s.settings, wield: w } })),
  setSingleHand: (h) =>
    set((s) => ({ settings: { ...s.settings, singleHand: h } })),
  setHeightMode: (h) =>
    set((s) => ({ settings: { ...s.settings, height: h } })),
  setCutArrowInverted: (value) => {
    saveLocalStorage("sq_cutArrowInverted", value ? "1" : "0");
    set((s) => ({ settings: { ...s.settings, cutArrowInverted: value } }));
  },
  setBloomEnabled: (enabled) => set({ bloomEnabled: enabled }),
  toggleBloom: () => set((s) => ({ bloomEnabled: !s.bloomEnabled })),
  setTelemetryOptOut: (value) => {
    saveLocalStorage("sq_telemetryOptOut", value ? "1" : "0");
    setTelemetryClientOptOut(value);
    set({ telemetryOptOut: value });
  },
  toggleSwordGlow: () =>
    set((s) => ({ swordGlowEnabled: !s.swordGlowEnabled })),
  setMenuCursor: (cursor) => set({ menuCursor: cursor }),
  setMenuExitConfirm: (open) =>
    set({ menuExitConfirm: open, menuExitCursor: "cancel" }),
  setMenuExitCursor: (cursor) => set({ menuExitCursor: cursor }),

  addScore: (points) =>
    set((s) => {
      const newScore = s.score + points;
      return { score: newScore, best: Math.max(newScore, s.best) };
    }),

  incrementCombo: () =>
    set((s) => {
      const combo = s.combo + 1;
      return { combo, peakCombo: Math.max(s.peakCombo, combo) };
    }),
  resetCombo: () => set({ combo: 0 }),
  addMiss: () => set((s) => ({ missCount: s.missCount + 1 })),
  setLastGrade: (g) => set({ lastGrade: g }),
  setTenouchi: (v) => set({ lastTenouchi: v }),
  setSeqBonus: (n) => set({ seqBonus: n }),
  recordGrade: (g) =>
    set((s) => ({
      gradeCounts: { ...s.gradeCounts, [g]: s.gradeCounts[g] + 1 },
    })),
  recordTargetEvent: (event) =>
    set((s) => ({ runTargetEvents: [...s.runTargetEvents, event] })),
  recordObstacleEvent: (event) =>
    set((s) => ({ runObstacleEvents: [...s.runObstacleEvents, event] })),
  setResultDetailsOpen: (open) => set({ resultDetailsOpen: open }),
  toggleResultDetails: () =>
    set((s) => ({ resultDetailsOpen: !s.resultDetailsOpen })),
  setResultShareStatus: (status) => set({ resultShareStatus: status }),
  setRunKataTotal: (n) => set({ runKataTotal: n }),
  recordKataLanded: (bonus) =>
    set((s) => ({
      runKataLanded: s.runKataLanded + 1,
      runKataBonus: s.runKataBonus + bonus,
    })),

  unlockKata: (id) =>
    set((s) => {
      if (s.unlockedKatas.includes(id)) return {};
      const next = [...s.unlockedKatas, id];
      saveLocalStorage("sq_unlockedKatas", JSON.stringify(next));
      return { unlockedKatas: next };
    }),

  setActiveKata: (id) => set({ activeKata: id }),

  resetGame: () =>
    set((s) => ({
      ...freshRunState(),
      phase: "menu",
      mode: "campaign",
      best: s.best,
      survivalElapsed: 0,
      survivalTrackIndex: 0,
      survivalIntensity: 0,
      survivalAudioStatus: "idle",
      survivalAudioLoaded: 0,
      survivalAudioTotal: 6,
      activeTutorialPhase: null,
      tutorialLineIndex: 0,
      tutorialFeedback: null,
      tutorialPracticeResetKey: 0,
      tutorialReviewOnly: false,
      menuExitConfirm: false,
      menuExitCursor: "cancel",
    })),
}));
